import { useState, useCallback } from 'react';
import { workshopAPI } from '../../services/api';

/**
 * useWorkshopAdmin - Workshop bookings CRUD.
 */
export const useWorkshopAdmin = () => {
  const [workshopBookings, setWorkshopBookings] = useState([]);
  const [workshopStats, setWorkshopStats] = useState(null);
  const [workshopLoading, setWorkshopLoading] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [newBooking, setNewBooking] = useState({
    contact_name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    team_size: '',
    preferred_date: '',
    interests: '',
  });

  const fetchWorkshopBookings = useCallback(async () => {
    setWorkshopLoading(true);
    try {
      const [bookings, stats] = await Promise.all([
        workshopAPI.getAllBookings(),
        workshopAPI.getStats(),
      ]);
      setWorkshopBookings(bookings || []);
      setWorkshopStats(stats);
    } catch (error) {
      console.error('Failed to fetch workshop bookings:', error);
      setWorkshopBookings([]);
    } finally {
      setWorkshopLoading(false);
    }
  }, []);

  const handleUpdateBookingStatus = useCallback(async (bookingId, newStatus) => {
    try {
      const updated = await workshopAPI.updateBooking(bookingId, { status: newStatus });
      setWorkshopBookings((prev) => prev.map((b) => (b.id === bookingId ? updated : b)));
    } catch (error) {
      console.error('Failed to update booking status:', error);
      alert('Error updating booking status');
    }
  }, []);

  const handleDeleteBooking = useCallback(async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await workshopAPI.deleteBooking(bookingId);
      setWorkshopBookings((prev) => prev.filter((b) => b.id !== bookingId));
    } catch (error) {
      console.error('Failed to delete booking:', error);
      alert('Error deleting booking');
    }
  }, []);

  const handleCreateBooking = useCallback(async () => {
    try {
      const created = await workshopAPI.createManualBooking({
        ...newBooking,
        team_size: newBooking.team_size ? parseInt(newBooking.team_size) : null,
      });
      setWorkshopBookings((prev) => [created, ...prev]);
      setShowAddBookingModal(false);
      setNewBooking({
        contact_name: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        team_size: '',
        preferred_date: '',
        interests: '',
      });
    } catch (error) {
      console.error('Failed to create booking:', error);
      alert('Error creating booking');
    }
  }, [newBooking]);

  return {
    workshopBookings,
    workshopStats,
    workshopLoading,
    showAddBookingModal,
    setShowAddBookingModal,
    expandedBooking,
    setExpandedBooking,
    newBooking,
    setNewBooking,
    fetchWorkshopBookings,
    handleUpdateBookingStatus,
    handleDeleteBooking,
    handleCreateBooking,
  };
};
